import { Button, Form, FormProps, Modal, Typography, notification } from "antd";
import { useState, useEffect } from "react";
import { RcFile, UploadFile } from "antd/lib/upload/interface";
import Flex from "../../../../components/Flex";
import AllFormPages from "./components/FormPages/AllFormPages";
import {
  submitCoverWizardData,
  uploadAttachments,
  uploadMeasurements,
} from "../../../../store/actions/orderFlowActionCreators";
import LeaveWizardConfirmationModal from "./components/LeaveWizardConfirmationModal";
import moment, { Moment } from "moment";
import { SubmitCoverWizardDto } from "../../../../store/actions/dtos/SubmitCoverWizardDto";
import { SubmittedOrderModal } from "./components/SubmittedOrderModal";
import { SubmittedOrderModalPayload } from "./components/SubmittedOrderModal";
import { values } from "lodash";

export type FlowType = "basic" | "advanced";

export type CoverWizardForm = {
  date: Moment;
  poNumber: string;
  jobName: string;
  address: string;
  city: string;
  stateCode: string | null;
  zip: string;
  quoteType: string | null;
  shippingName: string;
  shippingAddress: string;
  shippingCity: string;
  shippingStateCode: string | null;
  shippingZip: string;
  comments: string;
  measureWithMeyco: boolean;
  fileList: UploadFile[];
  useMeasuringServices: boolean;
  measurementsDocuments: UploadFile[];
  coverSpacing: string[];
  coverMaterial: string[];
  coverColor: string[];
  distanceFromAB: string;
  hasNonRemovableRails: string;
  nonRemovableRailsLocation: string;
  hasSpoutOnDeck: string;
  spoutLocation: string;
  fillSpoutHeight: string;
  fillSpoutBelowCoping: string;
  fillSpoutBelowCopingLocation: string;
  hasRadiusedCorners: string;
  cornerRadius: string;
  hasCutCorners: string;
  cutCornersRadius: string;
  spaCoverWanted: string;
  spaWallOrientation: string;
  spaPoolHeightDifference: string;
  spaHasOpenSpillway: string;
  spaSpillwayWidthLocation: string;
  spaCopingSize: string;
  spaCopingOd: string;
  spaCopingId: string;
  spaDesignPreferences: string[];
  hasVanishingEdge: string;
  copingToVanishingEdge: string;
  vanishingEdgeWidth: string;
  catchBasinCovered: string;
  separateCatchBasinCover: string;
  wallToCatchBasinHeight: string;
  slideOrRockBelowCoping: string;
  slideOrRockBelowCopingHeight: string;
  hasSunLedge: string;
  hasDeck: string;
  deckingTotalRaise: string;
  deckingPtA: string;
  deckingHeightA: string;
  deckingPtB: string;
  deckingHeightB: string;
  deckingAnchorMethod: string;
  deckingWallHardware: string;
  deckingIncludeCable: string;
  hasAutocover: string;
  autocoverNeedsToBeCovered: string;
  autocoverBoxWidth: string;
  autocoverDeckType: string;
  autocoverWidthBehindBox: string;
  soNum?: string;
  returnTypes?: string[] | string;
  planNumber?: string;
};

export const emptyCoverQuoteInitialValues: CoverWizardForm = {
  // Pool Owner Info Page
  date: moment(),
  poNumber: "",
  jobName: "",
  address: "",
  city: "",
  stateCode: null,
  zip: "",
  comments: "",
  quoteType: "Estimate",
  returnTypes: [],
  planNumber: "",
  // Shipping Information Page
  shippingName: "",
  shippingAddress: "",
  shippingCity: "",
  shippingStateCode: null,
  shippingZip: "",
  // Attach Files Page
  measureWithMeyco: false,
  fileList: [],
  useMeasuringServices: false,
  // Material/Spacing/Color Page
  coverSpacing: [],
  coverMaterial: [],
  coverColor: [],
  // Measurements Page
  measurementsDocuments: [],
  distanceFromAB: "",
  // Important Considerations Page
  hasNonRemovableRails: "no",
  nonRemovableRailsLocation: "",
  hasSpoutOnDeck: "no",
  spoutLocation: "",
  fillSpoutHeight: "",
  fillSpoutBelowCoping: "no",
  fillSpoutBelowCopingLocation: "",
  // Corners Page
  hasRadiusedCorners: "no",
  cornerRadius: "",
  hasCutCorners: "no",
  cutCornersRadius: "",
  // Spa Cover Page
  spaCoverWanted: "no",
  spaWallOrientation: "n/a",
  spaPoolHeightDifference: "",
  spaHasOpenSpillway: "no",
  spaSpillwayWidthLocation: "",
  spaCopingSize: "",
  spaCopingOd: "",
  spaCopingId: "",
  spaDesignPreferences: [],
  //Edges page
  hasVanishingEdge: "no",
  copingToVanishingEdge: "",
  vanishingEdgeWidth: "",
  catchBasinCovered: "no",
  separateCatchBasinCover: "no",
  wallToCatchBasinHeight: "",
  slideOrRockBelowCoping: "no",
  slideOrRockBelowCopingHeight: "",
  hasSunLedge: "no",
  //Decking page
  hasDeck: "no",
  deckingTotalRaise: "",
  deckingPtA: "",
  deckingHeightA: "",
  deckingPtB: "",
  deckingHeightB: "",
  deckingAnchorMethod: "best way for safety",
  deckingWallHardware: "pivoting snap hooks",
  deckingIncludeCable: "no",
  //Autocover page
  hasAutocover: "no",
  autocoverNeedsToBeCovered: "no",
  autocoverBoxWidth: "",
  autocoverDeckType: "",
  autocoverWidthBehindBox: "",
  soNum: "",
};

export type FormGroupTouched = {
  colorGroup: boolean;
  materialGroup: boolean;
  spacingGroup: boolean;
  vanishingEdgeGroup: boolean;
  basinGroup: boolean;
  slideOrRockGroup: boolean;
  sunLedgeGroup: boolean;
  nonRemovableRailsGroup: boolean;
  spoutGroupTouched: boolean;
  fillSpoutHeightGroup: boolean;
  spoutBelowCopingGroup: boolean;
  angledRadiusedCornersGroup: boolean;
  cutCornersGroup: boolean;
  spaCoverGroup: boolean;
  spaWallOffsetGroup: boolean;
  spaSpillwayGroup: boolean;
  spaCopingGroup: boolean;
  spaDesignPreferencesGroup: boolean;
  deckingGroup: boolean;
  deckingAnchorGroup: boolean;
  deckingHardwareGroup: boolean;
  deckingCableGroup: boolean;
  autocoverGroup: boolean;
  autocoverCoverGroup: boolean;
};

const initialGroupTouchedValues: FormGroupTouched = {
  colorGroup: false,
  materialGroup: false,
  spacingGroup: false,
  vanishingEdgeGroup: false,
  basinGroup: false,
  slideOrRockGroup: false,
  sunLedgeGroup: false,
  nonRemovableRailsGroup: false,
  spoutGroupTouched: false,
  fillSpoutHeightGroup: false,
  spoutBelowCopingGroup: false,
  angledRadiusedCornersGroup: false,
  cutCornersGroup: false,
  spaCoverGroup: false,
  spaWallOffsetGroup: false,
  spaSpillwayGroup: false,
  spaCopingGroup: false,
  spaDesignPreferencesGroup: false,
  deckingGroup: false,
  deckingAnchorGroup: false,
  deckingHardwareGroup: false,
  deckingCableGroup: false,
  autocoverGroup: false,
  autocoverCoverGroup: false,
};

export type StateCode = {
  id: string;
  stateName: string;
};

export type CoverWizardModalPayload = {
  initialValues: CoverWizardForm;
  startWithOrderTypePage?: boolean;
  quoteIsRGA?: boolean;
  hideReturnTypes?: boolean;
  isReplacementQuote?: boolean;
};

type CoverWizardProps = {
  modalPayloadState: [
    CoverWizardModalPayload | null,
    React.Dispatch<React.SetStateAction<CoverWizardModalPayload | null>>
  ];
  submittedModalPayloadState: [
    SubmittedOrderModalPayload | null,
    React.Dispatch<React.SetStateAction<SubmittedOrderModalPayload | null>>
  ];
  onSubmit?: () => void;
};

function formModelToPayload(
  valuesToSubmit: Omit<
    CoverWizardForm,
    "fileList" | "measurementsDocuments" | "date"
  >,
  isReplacementQuote: boolean = false
): SubmitCoverWizardDto {
  const payload: SubmitCoverWizardDto = {
    quoteType: valuesToSubmit.quoteType,
    date: new Date().toUTCString(),
    poNumber: valuesToSubmit.poNumber,
    jobName: valuesToSubmit.jobName,
    soNum: !!valuesToSubmit.soNum ? valuesToSubmit.soNum : undefined,
    isReplacement: false,
    isRepair: false,
    isAlteration: false,
    isReplacementQuote,
    referenceMeycoNumber: valuesToSubmit.planNumber ?? "",
    poolOwnerInfo: {
      address: valuesToSubmit.address,
      city: valuesToSubmit.city,
      stateCode: valuesToSubmit.stateCode,
      zip: valuesToSubmit.zip,
    },
    shippingInfo: {
      shippingName: valuesToSubmit.shippingName,
      shippingAddress: valuesToSubmit.shippingAddress,
      shippingCity: valuesToSubmit.shippingCity,
      shippingStateCode: valuesToSubmit.shippingStateCode,
      shippingZip: valuesToSubmit.shippingZip,
    },
    fileAttachmentInfo: {
      comments: valuesToSubmit.comments,
      measureWithMeyco: valuesToSubmit.measureWithMeyco,
      distanceFromAB: valuesToSubmit.distanceFromAB,
    },
    coverInfo: {
      coverSpacing: valuesToSubmit.coverSpacing,
      coverMaterial: valuesToSubmit.coverMaterial,
      coverColor: valuesToSubmit.coverColor,
      autocoverInfo: {
        autocoverNeedsToBeCovered:
          valuesToSubmit.autocoverNeedsToBeCovered === "yes",
        autocoverBoxWidth: valuesToSubmit.autocoverBoxWidth,
        autocoverDeckType: valuesToSubmit.autocoverDeckType,
        autocoverWidthBehindBox: valuesToSubmit.autocoverWidthBehindBox,
      },
    },
    poolDetails: {
      hasNonRemovableRails: valuesToSubmit.hasNonRemovableRails === "yes",
      nonRemovableRailsLocation: valuesToSubmit.nonRemovableRailsLocation,
      hasSpoutOnDeck: valuesToSubmit.hasSpoutOnDeck === "yes",
      spoutLocation: valuesToSubmit.spoutLocation,
      fillSpoutHeight: valuesToSubmit.fillSpoutHeight,
      fillSpoutBelowCoping: valuesToSubmit.fillSpoutBelowCoping === "yes",
      fillSpoutBelowCopingLocation: valuesToSubmit.fillSpoutBelowCopingLocation,
      hasRadiusedCorners: valuesToSubmit.hasRadiusedCorners === "yes",
      cornerRadius: valuesToSubmit.cornerRadius,
      hasCutCorners: valuesToSubmit.hasCutCorners === "yes",
      cutCornersRadius: valuesToSubmit.cutCornersRadius,
      hasVanishingEdge: valuesToSubmit.hasVanishingEdge === "yes",
      copingToVanishingEdge: valuesToSubmit.copingToVanishingEdge,
      vanishingEdgeWidth: valuesToSubmit.vanishingEdgeWidth,
      catchBasinCovered: valuesToSubmit.catchBasinCovered === "yes",
      separateCatchBasinCover: valuesToSubmit.separateCatchBasinCover,
      wallToCatchBasinHeight: valuesToSubmit.wallToCatchBasinHeight,
      slideOrRockBelowCoping: valuesToSubmit.slideOrRockBelowCoping,
      slideOrRockBelowCopingHeight: valuesToSubmit.slideOrRockBelowCopingHeight,
      hasSunLedge: valuesToSubmit.hasSunLedge === "yes",
    },
    spaInfo:
      valuesToSubmit.spaCoverWanted === "yes"
        ? {
            spaWallOrientation: valuesToSubmit.spaWallOrientation,
            spaPoolHeightDifference: valuesToSubmit.spaPoolHeightDifference,
            spaHasOpenSpillway: valuesToSubmit.spaHasOpenSpillway === "yes",
            spaSpillwayWidthLocation: valuesToSubmit.spaSpillwayWidthLocation,
            spaCopingSize: valuesToSubmit.spaCopingSize,
            spaCopingOd: valuesToSubmit.spaCopingOd,
            spaCopingId: valuesToSubmit.spaCopingId,
            spaDesignPreferences: valuesToSubmit.spaDesignPreferences,
          }
        : null,
    deckingInfo:
      valuesToSubmit.hasDeck === "yes"
        ? {
            deckingTotalRaise: valuesToSubmit.deckingTotalRaise,
            deckingPtA: valuesToSubmit.deckingPtA,
            deckingHeightA: valuesToSubmit.deckingHeightA,
            deckingPtB: valuesToSubmit.deckingPtB,
            deckingHeightB: valuesToSubmit.deckingHeightB,
            deckingAnchorMethod: valuesToSubmit.deckingAnchorMethod,
            deckingWallHardware: valuesToSubmit.deckingWallHardware,
            deckingIncludeCable: valuesToSubmit.deckingIncludeCable,
          }
        : null,
  };

  if (Array.isArray(valuesToSubmit?.returnTypes)) {
    valuesToSubmit.returnTypes.forEach((returnType) => {
      if (returnType === "replacement") {
        payload.isReplacement = true;
      } else if (returnType === "repair") {
        payload.isRepair = true;
      } else if (returnType === "alteration") {
        payload.isAlteration = true;
      }
    });
  } else if (typeof valuesToSubmit?.returnTypes == "string") {
    if (valuesToSubmit.returnTypes === "replacement") {
      payload.isReplacement = true;
    } else if (valuesToSubmit.returnTypes === "repair") {
      payload.isRepair = true;
    } else if (valuesToSubmit.returnTypes === "alteration") {
      payload.isAlteration = true;
    }
  }
  return payload;
}

const CoverWizardModal = ({
  modalPayloadState: [modalPayload, setModalPayload],
  submittedModalPayloadState: [submittedModalPayload, setSubmittedModalPayload],
  onSubmit,
}: CoverWizardProps & {
  submittedModalPayloadState: [
    SubmittedOrderModalPayload | null,
    React.Dispatch<React.SetStateAction<SubmittedOrderModalPayload | null>>
  ];
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [leaveWizardModalOpen, setLeaveWizardModalOpen] =
    useState<boolean>(false);
  const [form] = Form.useForm<CoverWizardForm>();
  const [wizardPageNumber, setWizardPageNumber] = useState<number>(
    modalPayload?.startWithOrderTypePage ? 1 : 2
  );

  //antd form does not support persisting touched state for fields that are unmounted and remounted
  //so we have to keep track of touched state for form groups manually
  const [formGroupTouched, setFormGroupTouched] = useState<FormGroupTouched>(
    initialGroupTouchedValues
  );

  const attemptSubmitForm: FormProps<CoverWizardForm>["onFinish"] =
    async () => {
      setSubmitting(true);

      //values are retrieved from the form instead of this function as
      // this function doesn't include values from previous form pages
      const {
        fileList,
        measurementsDocuments,
        date: _,
        ...valuesToSubmit
      } = form.getFieldsValue(true) as CoverWizardForm;

      const isReplacementQuote = modalPayload?.isReplacementQuote ?? false;

      const [submitCoverWizardRes, submitCoverWizardError] =
        await submitCoverWizardData(formModelToPayload(valuesToSubmit, isReplacementQuote));

      if (!!submitCoverWizardError || !submitCoverWizardRes) {
        notification.error({
          message: "Error submitting form",
          description:
            "There was an error submitting your form, please try again later.",
        });
        setSubmitting(false);
        return;
      }

      // Upload attachments from Attach Files page
      const files = fileList
        .filter((file) => !!file?.originFileObj)
        .map((file) => {
          return file.originFileObj as RcFile;
        });

      const [uploadAttachmentsRes, uploadAttachmentsError] =
        await uploadAttachments(
          files,
          submitCoverWizardRes.data.toString() ?? "",
          valuesToSubmit.comments,
          formModelToPayload(valuesToSubmit, isReplacementQuote)
        );

      if (!!uploadAttachmentsError) {
        setSubmitting(false);
        notification.error({
          message: "Error uploading attachments",
          description:
            "There was an error uploading your attachments, please try again later.",
        });
        return;
      }

      // Upload measurements from Measurements page
      const measurementsFiles = measurementsDocuments
        .filter((file) => !!file?.originFileObj)
        .map((file) => {
          return file.originFileObj as RcFile;
        });
      const [uploadMeasurementsRes, uploadMeasurementsError] =
        await uploadMeasurements(
          measurementsFiles,
          submitCoverWizardRes.data,
          valuesToSubmit.jobName,
          valuesToSubmit.quoteType ?? "",
          valuesToSubmit.distanceFromAB
        );

      if (!!uploadMeasurementsError) {
        setSubmitting(false);
        notification.error({
          message: "Error uploading measurements",
          description:
            "There was an error uploading your measurements, please try again later.",
        });
        return;
      }

      // If success
      onSubmit?.();
      setSubmitting(false);
      setSubmittedModalPayload({
        soNumber: submitCoverWizardRes.data.toString() ?? "",
        jobName: valuesToSubmit.jobName ?? "",
        isRga: modalPayload?.quoteIsRGA ?? false,
        formData: form.getFieldsValue(true) as CoverWizardForm, // Include the form data
      });
      handleCloseModal();
      // Else do nothing and tell user that data was not submitted
    };

  function handleCloseModal() {
    setLeaveWizardModalOpen(false);
    form.resetFields();
    setWizardPageNumber(1);
    setFormGroupTouched(initialGroupTouchedValues);
    setModalPayload(null);
  }

  return (
    <>
      <Modal
        title={""}
        open={!!modalPayload}
        footer={null}
        centered
        closable={submitting ? false : true}
        maskClosable={false}
        width={560}
        style={{ maxHeight: "97vh" }}
        keyboard={false}
        onCancel={() => {
          setLeaveWizardModalOpen(true);
        }}
      >
        <Form
          form={form}
          preserve
          layout="vertical"
          colon={false}
          name="poolCoverQuoteForm"
          initialValues={
            modalPayload?.initialValues ?? emptyCoverQuoteInitialValues
          }
          onFinish={attemptSubmitForm}
        >
          <AllFormPages
            quoteIsRGA={modalPayload?.quoteIsRGA ?? false}
            hideReturnTypes={modalPayload?.hideReturnTypes ?? false}
            startWithOrderTypePage={
              modalPayload?.startWithOrderTypePage ?? false
            }
            formGroupTouchedState={[formGroupTouched, setFormGroupTouched]}
            formIsSubmitting={submitting}
            wizardPageNumberState={[wizardPageNumber, setWizardPageNumber]}
          />
        </Form>
      </Modal>
      <LeaveWizardConfirmationModal
        modalIsOpenState={[leaveWizardModalOpen, setLeaveWizardModalOpen]}
        onProceed={handleCloseModal}
      />
    </>
  );
};

export default CoverWizardModal;
